import * as yup from 'yup';
import { AnySchema } from 'yup';
import {
  ComputeReservationDurationConfiguration,
  FlagSetting,
  GetExternalOrdersResponse,
  ReservationFlagTypeEnum,
} from '@api/ringil-api.v2.tsx';
import { isExternalIdPresent, orderSchema } from '@components/framework/orders/common.tsx';
import { isUnitCappedFlag, totalOrderUnits, unitLimitErrorMessage } from '@components/RingilComponents/form/flagUnitsLimit.ts';
import { CreateTimeslotForm } from '@components/ringil3/Features/Reservation/Sidedbars/types.ts';

type Translate = (key: string, defaultText?: string, data?: any, count?: number) => string;

export interface CreateTimeslotValidationDeps {
  tr: Translate;
  settings: ComputeReservationDurationConfiguration;
  loggedUserCompanyId: string;
  truckTypeRequired: boolean;
  showOrders: boolean;
  // RD-3010: orders come read-only from the linked expedition location, the form cannot fix them
  isReservationOrderLinkedToExpOrder: boolean;
  linkedExpeditionWithoutOrders: boolean;
  externalOrdersResponse?: GetExternalOrdersResponse;
}

/**
 * Validation for the create-reservation sidebar.
 *
 * Returned as a *factory* rather than a schema: whether a field is mandatory depends on which side
 * of the reservation the logged-in user is on, and that only becomes known once the form holds a
 * customer. `useForm` hands the data being validated to the factory, so `isOwner` is derived once
 * here instead of inside every rule.
 */
export const buildCreateTimeslotValidationSchema =
  ({
    tr,
    settings,
    loggedUserCompanyId,
    truckTypeRequired,
    showOrders,
    isReservationOrderLinkedToExpOrder,
    linkedExpeditionWithoutOrders,
    externalOrdersResponse,
  }: CreateTimeslotValidationDeps) =>
  (values: CreateTimeslotForm): AnySchema => {
    const isOwner = values?.companyLocationCustomerId === loggedUserCompanyId;
    const required = tr('validation.required', 'Value is required');
    const forRole = (ownerValue: boolean, counterPartyValue: boolean) => (isOwner ? ownerValue : counterPartyValue) === true;
    // .nullable() before .required() so an untouched null field reports "required" instead of yup's typeError.
    // The matches() rejects a whitespace-only value, which required() on its own accepts.
    const requiredIf = (isRequired: boolean) =>
      isRequired ? yup.string().nullable().required(required).matches(/\S/, required) : yup.string().nullable();

    const slotsRequired = forRole(settings?.slotsRequired, settings?.slotsRequiredCounterParty);
    const actionTypesVisible = forRole(settings?.ownerShowActionTypes, settings?.counterPartyShowActionTypes);
    const actionTypesRequired = actionTypesVisible && forRole(settings?.ownerActionTypeRequired, settings?.counterPartyActionTypeRequired);
    const atLeastOneActionType = tr('generic.atLeastOneHandlingType', 'Select at least one handling type');
    const flagsConfig = (settings?.flags ?? {}) as Partial<Record<ReservationFlagTypeEnum, FlagSetting>>;
    // Cross dock describes a share of the booked cargo, so it cannot outgrow it. Undefined where the
    // orders are not part of this form at all, which leaves the cap to the backend.
    const bookedUnits = showOrders === true ? totalOrderUnits(values?.orders) : undefined;

    return yup.object().shape({
      orders:
        showOrders !== true
          ? yup.object().nullable()
          : linkedExpeditionWithoutOrders
          ? yup.mixed().test('linkedExpeditionWithoutOrders', linkedExpeditionWithoutOrdersMessage(tr), () => false)
          : isReservationOrderLinkedToExpOrder
          ? yup.mixed().nullable()
          : orderSchema(isExternalIdPresent(externalOrdersResponse)),
      companyLocationId: yup.string().nullable().required(required),
      timeslot: slotsRequired
        ? yup.object().shape({
            rampId: yup.string().nullable().required(required),
            durationInMin: yup.number().nullable().typeError(required).required(required),
            arrivalHourString: yup.string().nullable().required(required),
            localDate: yup.string().nullable().required(required),
          })
        : yup.object().nullable(),
      cameraId: yup.string().when('openGate', {
        is: true,
        then: yup.string().nullable().required(required),
        otherwise: yup.string().nullable(),
      }),
      truckType: requiredIf(truckTypeRequired),
      actionTypes: actionTypesRequired
        ? yup.array().nullable().required(atLeastOneActionType).min(1, atLeastOneActionType)
        : yup.array().nullable(),
      driverInfo: yup.object().shape({
        driverName: requiredIf(forRole(settings?.driverNameRequired, settings?.driverNameRequiredCounterParty)),
        driverPhone: requiredIf(forRole(settings?.driverPhoneRequired, settings?.driverPhoneRequiredCounterParty)),
        driverId: requiredIf(forRole(settings?.driverIdRequired, settings?.driverIdRequiredCounterParty)),
      }),
      carPlate: requiredIf(forRole(settings?.carPlateRequired, settings?.carPlateRequiredCounterParty)),
      // RD-2977: the owner company can require every reservation to be filed in at least one folder
      folders:
        isOwner && settings?.foldersRequired === true
          ? yup.array().nullable().required(tr('CreateTimeslotSidebar.mandatoryFolder', 'Folder is required')).min(1, tr('CreateTimeslotSidebar.mandatoryFolder', 'Folder is required'))
          : yup.array().nullable(),
      flags: yup.array().of(
        yup.object().shape({
          reservationFlagType: yup.string().nullable().required(required),
          units: yup.lazy((_, { parent }) => {
            const flagType = parent?.reservationFlagType as ReservationFlagTypeEnum;
            const cfg = flagsConfig[flagType];
            const schema =
              cfg?.visibleAdditionalField && cfg?.additionalFieldRequired
                ? yup.number().nullable().typeError(required).required(required)
                : yup.number().nullable();
            return isUnitCappedFlag(flagType) && bookedUnits !== undefined ? schema.max(bookedUnits, unitLimitErrorMessage(tr, bookedUnits)) : schema;
          }),
        }),
      ),
    });
  };

export const linkedExpeditionWithoutOrdersMessage = (tr: Translate) =>
  tr(
    'CreateTimeslotSidebar.linkedExpeditionWithoutOrders',
    'The expedition has no order for this location. Orders are linked between the expedition and the reservation, so fill in the order in the expedition first.',
  );
